/**
 * Loading screen — reference-styled (legionnaire_loading_screen.html):
 * atmosphere layers, LEGIONNAIRE title, progress bar, phase label, and a
 * rotating historical tip. `setPhase` advances the bar; `done()` fades out.
 */
export interface LoadingPhase {
  id: string;
  label: string;
  /** 0..1 overall progress after this phase completes. */
  progress: number;
}

export const LOADING_PHASES: LoadingPhase[] = [
  { id: "icons", label: "Mustering the iconography", progress: 0.25 },
  { id: "data", label: "Reading the campaign archives", progress: 0.5 },
  { id: "models", label: "Raising the legion's likeness", progress: 0.75 },
  { id: "world", label: "Surveying the field of battle", progress: 1 },
];

const TIPS = [
  "A Roman century numbered eighty men, led by a centurion.",
  "The pilum was designed to bend on impact, denying it to the enemy.",
  "Frumentatio — foraging — kept the legions fed on campaign.",
  "A legion on the march could cover twenty miles in a day.",
  "The testudo turned shields into a tortoise-shell roof.",
  "Samnite warriors fought Rome for fifty years across three wars.",
];

export class LoadingScreen {
  private el: HTMLDivElement;
  private bar: HTMLDivElement;
  private phaseLabel: HTMLDivElement;
  private tipEl: HTMLDivElement;
  private tipTimer: number | null = null;
  private tipIdx = 0;

  constructor() {
    this.el = document.createElement("div");
    this.el.className = "lg-screen";
    this.el.id = "lg-loading";
    this.el.innerHTML =
      `<div class="lg-sky"></div><div class="lg-scrim"></div><div class="lg-vig"></div>` +
      `<div class="lg-body">` +
      `<div class="lg-crumb lg-anim">Senatus Populusque Romanus</div>` +
      `<div class="lg-title lg-anim lg-d1">LEGIONNAIRE</div>` +
      `<div class="lg-sub lg-anim lg-d1">Rome · Samnium · Gaul</div>` +
      `<div style="height:34px"></div>` +
      `<div class="lg-progress lg-anim lg-d2"><div></div></div>` +
      `<div style="height:12px"></div>` +
      `<div class="lg-note lg-anim lg-d2" id="lg-load-phase">Preparing…</div>` +
      `<div style="height:18px"></div>` +
      `<div class="lg-tip lg-anim lg-d3" id="lg-load-tip"></div>` +
      `</div>`;
    document.body.appendChild(this.el);
    this.bar = this.el.querySelector(".lg-progress > div") as HTMLDivElement;
    this.phaseLabel = this.el.querySelector("#lg-load-phase") as HTMLDivElement;
    this.tipEl = this.el.querySelector("#lg-load-tip") as HTMLDivElement;
    this.showTip();
    this.tipTimer = window.setInterval(() => this.showTip(), 5000);
  }

  private showTip(): void {
    this.tipIdx = (this.tipIdx + 1) % TIPS.length;
    this.tipEl.textContent = "❝ " + TIPS[this.tipIdx] + " ❞";
  }

  setPhase(id: string): void {
    const ph = LOADING_PHASES.find((p) => p.id === id);
    if (!ph) return;
    this.bar.style.width = `${Math.round(ph.progress * 100)}%`;
    this.phaseLabel.textContent = ph.label + "…";
  }

  /** Fade out and remove. Resolves when the fade completes. */
  done(): Promise<void> {
    return new Promise((resolve) => {
      if (this.tipTimer !== null) window.clearInterval(this.tipTimer);
      this.el.style.transition = "opacity .45s ease";
      this.el.style.opacity = "0";
      window.setTimeout(() => {
        this.el.remove();
        resolve();
      }, 480);
    });
  }
}
